import { Link } from "react-router-dom";

import { socialMediaLinks } from "simplydo/core/users";
import { MSTeamsIcon } from "simplydo/react/components/Branding/MSTeams";
import { Icon, Popup } from "simplydo/react/ui";
import type { SemanticICONS } from "simplydo/react/ui";

import { useAppSelector } from "store";

import type { ChipUser, SessionUser } from "./types";

const PROFILE_SOCIAL_KEYS = [
  "linkedin",
  "website",
  "facebook",
  "instagram",
  "github",
  "twitter",
] as const;

type ProfileSocialKey = (typeof PROFILE_SOCIAL_KEYS)[number];

type SocialLinksProps = { user: ChipUser; compact?: boolean };

export const SocialLinks = ({
  user: userProp,
  compact = false,
}: SocialLinksProps) => {
  const currentUser = useAppSelector(
    (state) => state.user,
  ) as SessionUser | null;

  let user: ChipUser = userProp;
  const hasProfile = user?.profile;
  if (!hasProfile) {
    user = { profile: { fullName: "Unknown User" } } as ChipUser;
  }

  const org = currentUser?.ownerOrganisation;

  const profileSocialElements = (
    hasProfile
      ? PROFILE_SOCIAL_KEYS.filter((key) => !!user?.profile?.[key])
      : []
  ) as ProfileSocialKey[];

  const teamsHandle = user?.teamsHandle;
  const canMessageViaTeams =
    !!currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "teamsHandleAdvertising",
    ) && !!teamsHandle;

  return (
    <div
      style={{ display: "inline-flex", alignItems: "center", marginLeft: 5 }}
    >
      {!user?.profile?.linkedin &&
        currentUser?._id === user._id &&
        org?.superadminTags?.innovationType === "externalInnovation" && (
          <Popup
            offset={[-10, 2]}
            content={<p>Add your LinkedIn</p>}
            trigger={
              <Link
                to="/preferences/profile"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon
                  name="linkedin"
                  style={{
                    marginLeft: "4px",
                    marginBottom: "2px",
                    color: "#8a8e90ff",
                  }}
                />
              </Link>
            }
          />
        )}

      {profileSocialElements.map((socialElement) => {
        const link = user?.profile?.[socialElement];
        if (!link) return null;
        const socialConfig = socialMediaLinks[socialElement];
        const fullLink =
          link.startsWith("https://") || link.startsWith("www.")
            ? link
            : socialConfig.link + link;

        return (
          <a
            key={socialElement}
            href={fullLink}
            target="blank"
            onClick={(e) => e.stopPropagation()}
            rel="noreferrer noopenner"
          >
            {"icon" in socialConfig && socialConfig.icon && (
              <Icon
                style={{ color: socialConfig.color }}
                name={socialConfig.icon as SemanticICONS}
              />
            )}
            {"iconLeftAlt" in socialConfig && socialConfig.iconLeftAlt && (
              <span
                style={{
                  fontSize: "1.15em",
                  color: "black",
                  padding: "0 0.1em",
                }}
              >
                {socialConfig.iconLeftAlt}
              </span>
            )}
          </a>
        );
      })}
      {canMessageViaTeams ? (
        <Popup
          content={<p>Message via Teams</p>}
          offset={[-12, 4]}
          trigger={
            <a
              key="teams"
              href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsHandle)}`}
              target="blank"
              onClick={(e) => e.stopPropagation()}
              rel="noreferrer noopenner"
            >
              <MSTeamsIcon
                style={{ marginRight: 5, marginBottom: compact ? -2 : -3 }}
                size={compact ? 13 : 22}
                color="#6264A7"
              />
            </a>
          }
        />
      ) : null}
    </div>
  );
};
